import { Dialog as DialogPrimitive, VisuallyHidden } from 'radix-ui'
import { XMarkIcon } from '@heroicons/react/24/solid'
import classNames from 'classnames'
import { Collapse, Expand } from 'iconoir-react'
import { twMerge } from 'tailwind-merge'
import { useConfirmation } from './ConfirmationContext'
import { useEscapeListener } from 'app/hooks/useEscapeListener'
import { minimizeModal, maximizeModal } from 'app/lib/modal'

const NOOP = () => {}
// Uses Radix primitives directly to replicate HeadlessModal behavior 1:1.
// Once all consumers migrate, we refactor to use ui/dialog.tsx properly.
import type { ModalProps } from './HeadlessModal'

export function RadixModal({
  open,
  title,
  subtitle,
  isFullScreen = true,
  is80Percent = false,
  children,
  onClose,
  showActions = false,
  actionLabel = `ok`,
  closable = true,
  titleClassName = ``,
  className,
  hideTitle = false,
  panelClassName,
  containerClassName,
  removePadding = false,
  overflowHidden = false,
  nestedModal,
  onActionTriggered = NOOP,
  isMinimized = false,
  showMinimizeButton = false,
  isNested = false,
  extraActions,
}: ModalProps): React.ReactElement {
  useEscapeListener(onClose, open)
  const [, isConfirmOpen] = useConfirmation()

  const handleClose = () => {
    if (isConfirmOpen) return
    onClose()
  }

  // modal={false} disables Radix's focus trap + scroll lock + pointer-events:none on body.
  // We use false for fullscreen (useEscapeListener handles Escape, no backdrop to click)
  // and for minimized (user interacts with page while modal is docked).
  // true for standard centered dialogs (backdrop click closes, focus trapped).
  const isModalMode = !isFullScreen && !isMinimized

  return (
    <DialogPrimitive.Root open={open || isMinimized} modal={isModalMode}>
      <DialogPrimitive.Portal forceMount>
        {(open || isMinimized) && (
          <DialogPrimitive.Content
            asChild
            onEscapeKeyDown={(e) => e.preventDefault()}
            onPointerDownOutside={(e) => e.preventDefault()}
            onInteractOutside={(e) => e.preventDefault()}
            onOpenAutoFocus={(e) => {
              if (isMinimized) e.preventDefault()
            }}
            onCloseAutoFocus={(e) => {
              if (isFullScreen) e.preventDefault()
            }}
          >
            <div className={twMerge(`dialog fixed inset-0 z-40`, className)}>
              {/* Backdrop overlay — only for non-fullscreen, non-minimized */}
              {!isFullScreen && !isMinimized && (
                <div
                  data-testid="modal-backdrop"
                  className="fixed inset-0 bg-(--scrim) transition-opacity"
                  onClick={handleClose}
                />
              )}

              <div
                className={twMerge(
                  classNames(
                    `fixed z-10 flex justify-center`,
                    // 12px on a phone, 24px from sm up: at 375px the wider gutter cost every dialog
                    // an eighth of its width.
                    { 'px-3 sm:px-6': isModalMode },
                    isFullScreen ? `items-start` : `items-center`,
                    isMinimized ? `bottom-3 left-3` : `inset-0`,
                  ),
                  containerClassName,
                )}
                onClick={(e) => {
                  // Close when clicking the container itself (outside the panel), not its children
                  if (e.target === e.currentTarget && !isFullScreen && !isMinimized) {
                    handleClose()
                  }
                }}
              >
                <div
                  className={classNames(
                    { 'max-h-[80%] min-h-[80%] w-full items-stretch md:w-[80%]': is80Percent },
                    { 'h-dvh w-screen': isFullScreen },
                    { 'items-end sm:items-center': !is80Percent },
                    `flex justify-center text-center sm:p-0`,
                  )}
                  onClick={(e) => {
                    if (e.target === e.currentTarget && !isFullScreen && !isMinimized) {
                      handleClose()
                    }
                  }}
                >
                  <div
                    data-surface="raised"
                    className={twMerge(
                      classNames(
                        isFullScreen ? `sm:w-full` : `max-h-[90vh]`,
                        `flex min-w-full flex-1 transform flex-col items-stretch rounded-xl bg-surface-raised text-left shadow-xl transition-all`,
                        isFullScreen && `min-h-full`,
                        { 'overflow-hidden': overflowHidden },
                      ),
                      panelClassName,
                    )}
                  >
                    {/* Radix warns without a Title and a Description; keep both even when the header is hidden */}
                    {hideTitle && (
                      <VisuallyHidden.Root asChild>
                        <DialogPrimitive.Title>{title || `Dialog`}</DialogPrimitive.Title>
                      </VisuallyHidden.Root>
                    )}
                    <DialogPrimitive.Description className="sr-only">Modal dialog</DialogPrimitive.Description>
                    {!hideTitle ? (
                      <div
                        className={classNames(
                          `flex h-20 shrink-0 items-center justify-between border-b border-default px-6 py-4`,
                          isMinimized && `rounded-b-lg`,
                        )}
                      >
                        <div>
                          <DialogPrimitive.Title
                            className={classNames(
                              `text-lg font-semibold leading-6 text-strong`,
                              isMinimized && `!text-base`,
                              titleClassName,
                            )}
                          >
                            {title}
                          </DialogPrimitive.Title>
                          {!!subtitle && <div className="text-sm capitalize text-subtle">{subtitle}</div>}
                        </div>
                        <div className="flex items-center gap-x-6">
                          {showMinimizeButton && !isNested && (
                            <button
                              aria-label={isMinimized ? `maximize` : `minimize`}
                              id="minimize-modal-btn"
                              onClick={() => (isMinimized ? maximizeModal() : minimizeModal())}
                              type="button"
                            >
                              {isMinimized ? (
                                <Expand className="h-5 w-5 cursor-pointer" />
                              ) : (
                                <Collapse className="h-5 w-5 cursor-pointer" />
                              )}
                            </button>
                          )}
                          {closable && (
                            <button aria-label="close" id="close-modal-btn" onClick={handleClose} type="button">
                              <XMarkIcon className="h-5 w-5 cursor-pointer" />
                            </button>
                          )}
                        </div>
                      </div>
                    ) : null}

                    <div
                      className={classNames(
                        `min-h-0 grow`,
                        { 'overflow-hidden': overflowHidden },
                        { 'overflow-y-auto': !overflowHidden },
                        { 'px-6 py-4': !removePadding },
                        isMinimized && `hidden`,
                      )}
                    >
                      {children}
                    </div>

                    {showActions && (
                      <div className="flex items-center justify-between border-t border-default px-6 py-3">
                        <div>{extraActions}</div>
                        <div className="flex gap-2">
                          <button aria-label="Cancel" onClick={handleClose} className="btn btn-secondary btn-md">
                            Cancel
                          </button>
                          <button onClick={onActionTriggered} className="btn btn-primary btn-md">
                            {actionLabel}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              {nestedModal && (
                <div onKeyDown={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
                  {nestedModal}
                </div>
              )}
            </div>
          </DialogPrimitive.Content>
        )}
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}
